import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';

interface DemoKey {
  gameId: number;
  level: number;
}

export const useCompletedDemosStore = defineStore('completedDemos', () => {
  // Use localStorage to persist completed demos between sessions
  // Structure: { 'gameId_level': true }
  const completedDemos = useLocalStorage<Record<string, boolean>>(
    'completedDemos',
    {}
  );

  /**
   * Marks a demo as completed for a specific game and level
   * @param gameId The ID of the game
   * @param level The level number
   */
  function markDemoCompleted(gameId: number, level: number): void {
    const key = `${gameId}_${level}`;
    completedDemos.value[key] = true;
  }

  /**
   * Checks if a demo has been completed for a specific game and level
   * @param gameId The ID of the game
   * @param level The level number
   * @returns True if the demo has been completed, false otherwise
   */
  function isDemoCompleted(gameId: number, level: number): boolean {
    const key = `${gameId}_${level}`;
    return !!completedDemos.value[key];
  }

  /**
   * Clears all completed demos data
   * Used primarily for testing or when a user logs out
   */
  function clearCompletedDemos(): void {
    Object.keys(completedDemos.value).forEach((key) => {
      delete completedDemos.value[key];
    });
  }

  /**
   * Gets all completed demos for a specific game
   * @param gameId The ID of the game
   * @returns An array of level numbers
   */
  function getCompletedDemosForGame(gameId: number): number[] {
    return Object.keys(completedDemos.value)
      .filter((key) => key.startsWith(`${gameId}_`))
      .map((key) => parseInt(key.split('_')[1]));
  }

  return {
    // State
    completedDemos,

    // Actions
    markDemoCompleted,
    isDemoCompleted,
    clearCompletedDemos,
    getCompletedDemosForGame,
  };
});
